import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { ComponentDesign, Direction2D } from '@paragon/contract-library';
import { componentDesignsDeletedWithInsertions } from '@paragon/layout-models';

import {
  applicationOnPickingReferenceLineIntersection,
  clearPickingObject,
  startInsertMember,
} from './applicationSlice';
import { commitBearing, commitMembers } from './componentDesignsSlice';

interface UndoSnapshot {
  readonly state: ComponentDesign;
  readonly previous: UndoSnapshot | null;
}

interface RedoSnapshot {
  readonly state: ComponentDesign;
  readonly next: RedoSnapshot | null;
}

// Contains past (undo) and future (for redo-ing undone changes) snapshots of a component design's state.  A snapshot
//   should be created before (not after) starting an action that can be undone, and the component design's current
//   state is not stored in either part of the chain.  This approach allows us to make some changes without creating a
//   snapshot, and ensures that those changes will be undone as well when Ctrl+Z is pressed.
export interface UndoRedoChain {
  readonly undo: UndoSnapshot | null;
  readonly redo: RedoSnapshot | null;
}

export const EDIT_IN_PROGRESS_TYPES = [
  'EditingReferenceLineIntersection',
  'EditingBearing',
  'EditingPlatePair',
  'EditingAnalogJoint',
  'EditingReferenceLineOffset',
  'EditingProfileJoint',
  'EditingWebJoint',
  'EditingSplice',
  'InsertingMember',
] as const;
export type EditingType = (typeof EDIT_IN_PROGRESS_TYPES)[number];

export const coordinateSystems = ['world', 'local'] as const;
export type CoordinateSystem = (typeof coordinateSystems)[number];

export interface Editor {
  readonly undoRedoChains: { readonly [componentDesignGuid: string]: UndoRedoChain | undefined };
  readonly editInProgress: EditingType | false;
  readonly coordinateSystem: CoordinateSystem;
  readonly snappingDistance: number;
  readonly coordinateUpDirection: Direction2D;
  readonly getObjectCoordinate: boolean;
}

const initialState: Editor = {
  undoRedoChains: {},
  editInProgress: false,
  coordinateSystem: 'world',
  snappingDistance: 1 / 16,
  coordinateUpDirection: { x: 0, y: 1 },
  getObjectCoordinate: false,
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setEditInProgressType: (state, action: PayloadAction<EditingType>) => {
      state.editInProgress = action.payload;
    },
    clearEditInProgress: (state) => {
      state.editInProgress = false;
    },
    pushComponentDesignUndoState: (state, action: PayloadAction<ComponentDesign>) => {
      const chain = state.undoRedoChains[action.payload.guid];

      if (chain?.undo?.state !== action.payload) {
        state.undoRedoChains[action.payload.guid] = {
          undo: { state: castDraft(action.payload), previous: chain?.undo ?? null },
          redo: null,
        };
      }
    },
    /**
     * Replaces a component design's undo history, such as to make several changes undo as one, or to put back the
     *   history from before changes that were themselves put back. `undefined` leaves it with no history.
     */
    componentDesignUndoRedoChainReplaced: (
      state,
      action: PayloadAction<{ componentDesignGuid: string; chain: UndoRedoChain | undefined }>,
    ) => {
      const { componentDesignGuid, chain } = action.payload;
      if (chain == null) delete state.undoRedoChains[componentDesignGuid];
      else state.undoRedoChains[componentDesignGuid] = castDraft(chain);
    },
    undoComponentDesign: (
      state,
      action: PayloadAction<{ componentDesignGuid: string; current: ComponentDesign; target: ComponentDesign }>,
    ) => {
      const { componentDesignGuid, current, target } = action.payload;

      if (componentDesignGuid !== current.guid || componentDesignGuid !== target.guid) {
        throw new Error('Invalid undo state—this should never occur.');
      }

      const chain = state.undoRedoChains[componentDesignGuid];
      if (current && (current === chain?.undo?.state || current === chain?.redo?.state)) {
        throw new Error('Invalid undo state—this should never occur.');
      }

      state.undoRedoChains[componentDesignGuid] = {
        undo: chain!.undo!.previous,
        redo: { state: castDraft(current), next: chain!.redo },
      };
    },
    redoComponentDesign: (
      state,
      action: PayloadAction<{ componentDesignGuid: string; current: ComponentDesign; target: ComponentDesign }>,
    ) => {
      const { componentDesignGuid, current, target } = action.payload;

      if (componentDesignGuid !== current.guid || componentDesignGuid !== target.guid) {
        throw new Error('Invalid undo state—this should never occur.');
      }

      const chain = state.undoRedoChains[componentDesignGuid];
      if (current && (current === chain?.undo?.state || current === chain?.redo?.state)) {
        throw new Error('Invalid undo state—this should never occur.');
      }

      state.undoRedoChains[componentDesignGuid] = {
        undo: { state: castDraft(current), previous: chain!.undo },
        redo: chain!.redo!.next,
      };
    },
    changeEditorCoordinateSystem: (state, action: PayloadAction<CoordinateSystem>) => {
      state.coordinateSystem = action.payload;
    },
    changeEditorSnappingDistance: (state, action: PayloadAction<number>) => {
      state.snappingDistance = action.payload;
    },
    setLocalCoordinateDirection: (state, action: PayloadAction<Direction2D>) => {
      state.coordinateUpDirection = action.payload;
    },
    toggleGetObjectCoordinateSystem: (state) => {
      state.getObjectCoordinate = !state.getObjectCoordinate;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(componentDesignsDeletedWithInsertions, (state, action) => {
      for (const componentDesignGuid of action.payload.guids) {
        delete state.undoRedoChains[componentDesignGuid];
      }
    });

    builder.addCase(startInsertMember, (state) => {
      state.editInProgress = 'InsertingMember';
    });

    builder.addCase(applicationOnPickingReferenceLineIntersection, (state) => {
      state.editInProgress = 'EditingReferenceLineIntersection';
    });

    builder.addCase(clearPickingObject, (state) => {
      if (state.editInProgress === 'EditingReferenceLineIntersection') {
        state.editInProgress = false;
      }
    });

    builder.addCase(commitMembers, (state) => {
      state.editInProgress = false;
    });

    builder.addCase(commitBearing, (state) => {
      state.editInProgress = false;
    });
  },
});

export const {
  setEditInProgressType,
  clearEditInProgress,
  pushComponentDesignUndoState,
  componentDesignUndoRedoChainReplaced,
  undoComponentDesign,
  redoComponentDesign,
  changeEditorCoordinateSystem,
  changeEditorSnappingDistance,
  setLocalCoordinateDirection,
  toggleGetObjectCoordinateSystem,
} = editorSlice.actions;
export const editor = editorSlice.reducer;
